<template>
  <a-modal
    v-model="visibleModal"
    @cancel="handleCancel"
    :maskClosable="false"
    :title="
      mode === 'add' ? $t('product.addProduct') : $t('product.editProduct')
    "
    :width="1000"
    :footer="null"
  >
    <a-form :form="form" @submit="handleSubmit">
      <!-- Row 1: Product Name and Price -->
      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-item :label="$t('product.name')">
            <a-input
              v-decorator="[
                'name',
                { rules: [{ required: true, message: $t('requis.nom') }] },
              ]"
              :placeholder="$t('product.name')"
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item :label="$t('product.price')">
            <a-input-number
              v-decorator="[
                'price',
                {
                  rules: [{ required: true, message: $t('product.reqprice') }],
                },
              ]"
              :placeholder="$t('product.price')"
              :min="0"
              style="width: 100%"
            />
          </a-form-item>
        </a-col>
      </a-row>

      <!-- Row 2: Availability and Reference -->
      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-item :label="$t('product.availability')">
            <a-select
              v-decorator="[
                'availability',
                {
                  rules: [
                    { required: true, message: $t('product.reqavailability') },
                  ],
                },
              ]"
              :placeholder="$t('product.availability')"
            >
              <a-select-option value="available">{{
                $t("product.available")
              }}</a-select-option>
              <a-select-option value="soon">{{
                $t("product.comingSoon")
              }}</a-select-option>
              <a-select-option value="out">{{
                $t("product.outOfStock")
              }}</a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item :label="$t('product.reference')">
            <a-input
              v-decorator="['reference', { rules: [{ required: false }] }]"
              :placeholder="$t('product.ref')"
            />
          </a-form-item>
        </a-col>
      </a-row>

      <!-- Row 3: Quantity and Tags -->
      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-item :label="$t('product.quantity')">
            <a-input-number
              v-decorator="[
                'quantity',
                {
                  rules: [
                    { required: true, message: $t('product.reqquantity') },
                  ],
                },
              ]"
              :placeholder="$t('product.quantity')"
              :min="0"
              style="width: 100%"
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item :label="$t('product.tags')">
            <a-select
              mode="tags"
              :placeholder="$t('product.tag')"
              v-decorator="[
                'tags',
                { rules: [{ required: true, message: $t('requis.reqtags') }] },
              ]"
            >
              <a-select-option
                v-for="tag in existingTags"
                :key="tag"
                :value="tag"
              >
                {{ tag }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
      </a-row>

      <!-- Row 4: Description -->
      <a-row :gutter="16">
        <a-col :span="24">
          <a-form-item
            :label="$t('product.description')"
            class="quill-editor-container"
          >
            <quill-editor
              v-model="description"
              :options="editorOptions"
              @input="descriptionError = false"
            />
            <span
              style="color: #f5222e; font-size: 14px"
              v-if="descriptionError"
              >{{ $t("requis.reqdescription") }}
            </span>
          </a-form-item>
        </a-col>
      </a-row>

      <!-- Row 5: Cover Picture and Additional Pictures -->
      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-item :label="$t('product.coverPicture')">
            <a-upload-dragger
              :file-list="coverPicture"
              :multiple="false"
              :custom-request="handleCustomRequest"
              :before-upload="beforeUploadCoverPicture"
              accept=".jpg,.jpeg,.png"
              list-type="picture"
              @change="handleChangeUploadCoverPicture"
              :auto-upload="false"
            >
              <p class="ant-upload-drag-icon">
                <a-icon type="inbox" />
              </p>
              <p class="ant-upload-text">
                {{ $t("product.uploadCoverPicture") }}
              </p>
              <p class="ant-upload-hint">{{ $t("actualite.cliquer") }}</p>
            </a-upload-dragger>
            <span
              style="color: #f5222e; font-size: 14px"
              v-if="coverPictureUploaded"
              >{{ $t("requis.reqcoverpicture") }}
            </span>
            <a-progress
              v-if="coverPictureProgress.value > 0"
              :percent="coverPictureProgress.value"
              size="small"
              :stroke-color="{
                from: '#108ee9',
                to: '#87d068',
              }"
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item :label="$t('product.pictures')">
            <a-upload-dragger
              list-type="picture"
              :file-list="fileList"
              :before-upload="beforeUploadContent"
              :custom-request="handleCustomRequest"
              :multiple="true"
              accept=".jpg,.jpeg,.png"
              @change="handleChangeUpload"
              :auto-upload="false"
            >
              <p class="ant-upload-drag-icon">
                <a-icon type="inbox" />
              </p>
              <p class="ant-upload-text">
                {{ $t("library.uploadAttachment") }}
              </p>
              <p class="ant-upload-hint">
                {{ $t("actualite.cliquer") }}
              </p>
            </a-upload-dragger>
            <a-progress
              v-if="picturesProgress.value > 0"
              :percent="picturesProgress.value"
              size="small"
              :stroke-color="{
                from: '#108ee9',
                to: '#87d068',
              }"
            />
          </a-form-item>
        </a-col>
      </a-row>

      <!-- Submit and Cancel Buttons -->
      <a-form-item>
        <a-button
          type="primary"
          :loading="loading"
          :disabled="disabled"
          htmlType="submit"
          style="margin-right: 10px"
        >
          {{ mode === "add" ? $t("product.submit") : $t("action.modifier") }}
        </a-button>
        <a-button type="default" :disabled="disabled" @click="handleCancel">{{
          $t("action.annuler")
        }}</a-button>
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script>
import "quill/dist/quill.snow.css";
import { quillEditor } from "vue-quill-editor";
import apiClient from "@/services/axios";
import MagicUrl from "quill-magic-url";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { uploadFilesToS3 } from "@/services/uploadService";
import { mapState } from "vuex";

Quill.register("modules/magicUrl", MagicUrl);

export default {
  name: "ProductForm",
  components: {
    quillEditor,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ["add", "edit"].includes(value),
    },
    productData: {
      type: Object,
      default: () => ({}),
    },
    existingTags: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState(["settings", "user"]),
  },
  data() {
    return {
      visibleModal: false,
      form: null,
      description: "",
      fileList: [],
      coverPicture: [],
      tagNames: [],
      loading: false,
      coverPictureUploaded: false,
      descriptionError: false,
      disabled: false,
      coverPictureProgress: { value: 0 },
      picturesProgress: { value: 0 },
      editorOptions: {
        theme: "snow",
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            ["blockquote", "code-block"],
            [{ header: 1 }, { header: 2 }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "sub" }, { script: "super" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ direction: "rtl" }],
            [{ size: ["small", false, "large", "huge"] }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ font: [] }],
            [{ align: [] }],
            ["clean"],
          ],
          magicUrl: true,
        },
      },
    };
  },
  watch: {
    visible(newVal) {
      this.visibleModal = newVal;
    },
    productData: {
      immediate: true,
      handler(product) {
        if (product && Object.keys(product).length > 0) {
          this.$nextTick(() => {
            this.form.setFieldsValue({
              name: product.name,
              price: product.price,
              availability: product.availability,
              reference: product.reference,
              quantity: product.quantity,
              tags: product.tags ? product.tags.map((tag) => tag.name) : [],
            });
          });

          this.description = product.description || "";
          this.tagNames = product.tags
            ? product.tags.map((tag) => tag.name)
            : [];

          if (product.coverPicture) {
            this.coverPicture = [
              {
                uid: "-1",
                name: this.getAttachmentName(product.coverPicture),
                status: "done",
                url: this.settings.resourcesUrl.replace(
                  "FILE_PATH",
                  product.coverPicture
                ),
                thumbUrl: this.settings.resourcesUrl.replace(
                  "FILE_PATH",
                  product.coverPicture
                ),
              },
            ];
          }

          if (product.pictures && Array.isArray(product.pictures)) {
            this.fileList = product.pictures.map((pictureUrl, index) => ({
              uid: index,
              name: this.getAttachmentName(pictureUrl),
              status: "done",
              url: this.settings.resourcesUrl.replace("FILE_PATH", pictureUrl),
              thumbUrl: this.settings.resourcesUrl.replace(
                "FILE_PATH",
                pictureUrl
              ),
              originalPath: pictureUrl, // Store the original path
            }));
          }
        }
      },
    },
  },
  mounted() {
    this.form = this.$form.createForm(this);

    if (this.mode === "edit" && this.productData) {
      if (this.productData.coverPicture) {
        this.coverPicture = [
          {
            uid: "-1",
            name: "coverPicture",
            status: "done",
            url: this.productData.coverPicture,
            thumbUrl: this.productData.coverPicture,
          },
        ];
      }

      if (
        this.productData.pictures &&
        Array.isArray(this.productData.pictures)
      ) {
        this.fileList = this.productData.pictures.map((picture, index) => ({
          uid: `${index}`,
          name: `picture-${index}`,
          status: "done",
          url: picture,
          thumbUrl: picture,
        }));
      }
    }
  },
  methods: {
    handleCustomRequest({ file, onSuccess }) {
      // Simulate uploading process
      setTimeout(() => {
        onSuccess("ok");
      }, 1000);
    },

    getAttachmentName(url) {
      const parts = url.split("/");
      const fileNameWithType = parts[parts.length - 1];
      const decodedFileNameWithType = decodeURIComponent(fileNameWithType);
      const regex = /^\d+--(.+)$/;
      const match = decodedFileNameWithType.match(regex);
      const extractedName = match ? match[1] : decodedFileNameWithType;

      const lastDotIndex = extractedName.lastIndexOf(".");
      const fileName =
        lastDotIndex !== -1
          ? extractedName.slice(0, lastDotIndex)
          : extractedName;
      const fileType =
        lastDotIndex !== -1 ? extractedName.slice(lastDotIndex + 1) : "";

      return `${fileName}.${fileType}`;
    },

    handleCancel() {
      this.form.resetFields();
      this.visibleModal = false;
      this.fileList = [];
      this.coverPicture = [];
      this.description = "";
      this.coverPictureUploaded = false;
      this.descriptionError = false;

      this.$emit("close");
    },

    beforeUploadCoverPicture(file) {
      const isImage = ["image/jpeg", "image/png", "image/jpg"].includes(
        file.type
      );
      if (!isImage) {
        this.$message.error(this.$t("library.fileUploadError"));
        return false;
      }

      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.$message.error(this.$t("error.image"));
        return false;
      }

      this.coverPicture = [
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
          thumbUrl: URL.createObjectURL(file),
          originFileObj: file,
        },
      ];
      return false;
    },

    beforeUploadContent(file, fileList) {
      // Check file type
      const isImage = ["image/jpeg", "image/png", "image/jpg"].includes(
        file.type
      );
      if (!isImage) {
        this.$message.error(this.$t("error.fichiers"));
        return false;
      }

      // Check total files count
      const totalFiles = this.fileList.length + 1; // Count current file
      if (totalFiles > 15) {
        this.$message.error(this.$t("library.maxDocs"));
        return false;
      }

      let maxSize = 0;
      this.fileList.forEach((f) => {
        if (f.size) {
          maxSize += f.size;
        }
      });
      maxSize += file.size;
      maxSize = maxSize / 1024 / 1024;

      // Check total size (50MB)
      if (maxSize > 50) {
        this.$message.error(this.$t("error.image50"));
        return false;
      }

      this.fileList = [
        ...this.fileList,
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
          thumbUrl: URL.createObjectURL(file),
          originFileObj: file,
          size: file.size,
        },
      ];

      // Return false to prevent default upload behavior
      return false;
    },

    handleChangeUploadCoverPicture(info) {
      const { status } = info.file;
      this.coverPictureUploaded = false;
      this.disabled = status === "uploading";
      if (status === "removed") {
        this.coverPicture = [];
      }
    },

    handleChangeUpload(info) {
      const { status } = info.file;
      this.disabled = status === "uploading";

      if (status === "removed") {
        this.fileList = this.fileList.filter(
          (item) => item.uid !== info.file.uid
        );
      }

      // Additional validation when a file is added
      if (status !== "removed") {
        // Calculate total size of all files
        let totalSize = 0;
        this.fileList.forEach((f) => {
          if (f.size) {
            totalSize += f.size;
          } else if (f.originFileObj && f.originFileObj.size) {
            totalSize += f.originFileObj.size;
          }
        });

        const totalSizeMB = totalSize / 1024 / 1024;

        // Check if exceeds 50MB limit
        if (totalSizeMB > 50) {
          this.$message.error(this.$t("error.image50"));
          // Remove the last added file
          this.fileList = this.fileList.filter(
            (item) => item.uid !== info.file.uid
          );
        }
      }
    },

    async handleSubmit(e) {
      e.preventDefault();

      this.descriptionError = !this.description;
      if (this.coverPicture.length === 0) {
        this.coverPictureUploaded = true;
      }
      this.form.validateFields((err, values) => {
        if (!err && !this.coverPictureUploaded && !this.descriptionError) {
          this.loading = true;
          this.disabled = true;
          // Initialize progress trackers
          this.coverPictureProgress = { value: 0 };
          this.picturesProgress = { value: 0 };

          if (this.mode === "add") {
            this.addProduct(values);
          } else {
            this.editProduct(values);
          }
        }
      });
    },

    async addProduct(values) {
      try {
        // Handle file uploads to S3 first
        const uploadPromises = [];
        const uploadResults = {
          coverPicture: null,
          pictures: [],
        };

        // Prepare cover picture upload if exists
        if (this.coverPicture.length > 0) {
          const coverFile =
            this.coverPicture[0].originFileObj || this.coverPicture[0];
          uploadPromises.push(
            uploadFilesToS3(
              [coverFile],
              "marketplace/coverPicture",
              this.coverPictureProgress
            ).then((keys) => {
              uploadResults.coverPicture = keys[0];
            })
          );
        }

        // Prepare additional pictures upload
        if (this.fileList.length > 0) {
          const picturesToUpload = this.fileList.map(
            (file) => file.originFileObj || file
          );

          uploadPromises.push(
            uploadFilesToS3(
              picturesToUpload,
              "marketplace/pictures",
              this.picturesProgress
            ).then((keys) => {
              uploadResults.pictures = keys;
            })
          );
        }

        // Execute all uploads in parallel
        await Promise.all(uploadPromises);

        // Prepare product data
        const productData = {
          name: values.name,
          price: values.price,
          availability: values.availability,
          quantity: values.quantity,
          description: this.description,
        };

        if (values.reference) {
          productData.reference = values.reference;
        }

        if (uploadResults.coverPicture) {
          productData.coverPicture = uploadResults.coverPicture;
        }

        if (uploadResults.pictures.length > 0) {
          productData.pictures = uploadResults.pictures;
        }

        if (Array.isArray(values.tags)) {
          productData.tags = values.tags;
        }

        // Send product data to backend
        const res = await apiClient.put("/marketplace/product/", productData);
        this.$message.success(this.$t("product.productAdded"));
        this.handleCancel();
        this.$emit("submit", res.data);
      } catch (e) {
        this.$message.error(this.$t("product.impoPubAjout"));
      } finally {
        this.loading = false;
        this.disabled = false;
        this.coverPictureProgress = { value: 0 };
        this.picturesProgress = { value: 0 };
      }
    },

    async editProduct(values) {
      try {
        // Only include changed fields
        const productData = {};
        const originalData = this.productData;

        // Check for changes in basic fields by comparing with original data
        if (values.name && values.name !== originalData.name) {
          productData.name = values.name;
        }

        if (values.price && values.price !== originalData.price) {
          productData.price = values.price;
        }

        if (
          values.availability &&
          values.availability !== originalData.availability
        ) {
          productData.availability = values.availability;
        }

        if (values.quantity && values.quantity !== originalData.quantity) {
          productData.quantity = values.quantity;
        }

        if (values.reference && values.reference !== originalData.reference) {
          productData.reference = values.reference;
        }

        if (this.description && this.description !== originalData.description) {
          productData.description = this.description;
        }

        // Handle tags - compare with original tags
        if (values.tags && Array.isArray(values.tags)) {
          const originalTags = originalData.tags
            ? originalData.tags.map((tag) => tag.name)
            : [];
          const newTags = values.tags;

          // Only update if tags have changed
          if (
            JSON.stringify(newTags.sort()) !==
            JSON.stringify(originalTags.sort())
          ) {
            productData.tags = newTags;
          }
        }

        // Check if files have been modified (added or removed)
        const originalFileCount = originalData.pictures
          ? originalData.pictures.length
          : 0;
        const currentFileCount = this.fileList.length;
        const filesChanged = originalFileCount !== currentFileCount;

        // If files were removed but no new files added, we need to include the current file list
        if (filesChanged && !this.fileList.some((file) => file.originFileObj)) {
          productData.pictures = this.fileList.map(
            (file) => file.originalPath || file.url
          );
        }

        // Handle file uploads to S3 first
        const uploadPromises = [];

        // Handle cover picture update - only if new file is selected
        if (
          this.coverPicture &&
          this.coverPicture.length > 0 &&
          this.coverPicture[0].originFileObj
        ) {
          uploadPromises.push(
            uploadFilesToS3(
              [this.coverPicture[0].originFileObj],
              "marketplace/coverPicture",
              this.coverPictureProgress
            ).then((keys) => {
              productData.coverPicture = keys[0];
            })
          );
        }

        // Handle pictures - only upload new ones and keep existing ones
        const newPictures = this.fileList.filter((file) => file.originFileObj);
        if (newPictures.length > 0) {
          uploadPromises.push(
            uploadFilesToS3(
              newPictures.map((file) => file.originFileObj),
              "marketplace/pictures",
              this.picturesProgress
            ).then((keys) => {
              // Get existing picture URLs
              const existingPictures = this.fileList
                .filter((file) => file.url && !file.originFileObj)
                .map((file) => file.originalPath || file.url);

              productData.pictures = [...existingPictures, ...keys];
            })
          );
        }

        // Execute all uploads in parallel
        await Promise.all(uploadPromises);

        // Only proceed with update if there are actual changes
        if (Object.keys(productData).length > 0) {
          await apiClient.put(
            `/marketplace/product/${originalData._id}`,
            productData
          );
          this.$message.success(this.$t("success.productUpdate"));
          this.$emit("productUpdated");
        } else {
          this.$message.info(this.$t("product.noChanges"));
        }

        this.handleCancel();
      } catch (e) {
        console.error(e.response || e.message);
        this.$message.error(this.$t("error.productUpdate"));
      } finally {
        this.loading = false;
        this.disabled = false;
        this.coverPictureProgress = { value: 0 };
        this.picturesProgress = { value: 0 };
      }
    },
  },
};
</script>

<style scoped>
.quill-editor-container {
  margin-bottom: 24px;
}
.ant-upload-text {
  margin-top: 8px;
}
</style>
